import styled from "styled-components";

import { ReactNode } from "react";

import { StyledListItem } from "../ListItem/ListItem";
import Icon, { IconWeight } from "../Icon/Icon";
import { IconAlignment } from "../List/list.type";
import { LayoutProps } from "styled-system";

interface ListItemWithIconProps {
  /**
    The name of the svg icon to be displayed in the left side of the list item text. This prop should be used only if you need to specify a different icon for the list item, otherwise the icon should be set at the List level.
    Accepts any fontawesome icon name: see https://fontawesome.com/icons?d=gallery.
 */
  icon?: string;

  /**
  The icon weight. It can be one of the following values: "regular" | "solid" | "light"
  */
  iconWeight?: IconWeight;

  /**
    The color of list item icon.
 */
  iconColor?: string;

  /**
   * Width of element. Can be in pixels or number or standard css keywords("auto", "100%").
   * Also accepts responsive values as an array. Each value coresponds to theme breakpoints.
   * Example: ["100%", 300, "50%", "auto"]
   */
  iconWidth?: LayoutProps["width"];

  /**
   * Height of element. Can be in pixels or number or standard css keywords("auto", "100%").
   * Also accepts responsive values as an array. Each value coresponds to theme breakpoints.
   * Example: ["100%", 300, "50%", "auto"]
   */
  iconHeight?: LayoutProps["height"];

  /**
  Allows the user to specify any custom styles.
  */
  css?: string;

  /**
  The text of the list item.
  */
  children: ReactNode;
  /**
   * The vertical alignment of the icon
   */
  iconAlignment?: IconAlignment;
}

type AlignItems = "flex-start" | "center" | "flex-end";

const StyledListItemWithIcon = styled(StyledListItem)<
  Partial<ListItemWithIconProps> & {
    alignItems: AlignItems;
  }
>`
  padding: 4px 0px;
  align-items: ${(props) => props.alignItems};
`;

const StyledItem = styled.span`
  flex: 1;
`;

const alignmentMapper = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};

const oldIconMapper = {
  checkmark: {
    name: "check",
    weight: "solid",
    width: "15px",
    height: "15px",
  },
};

export default function ListItemWithIcon({
  icon,
  iconWeight = "regular",
  iconColor = "primary-light-blue",
  iconAlignment = "center",
  iconWidth,
  iconHeight,
  children,
  ...props
}: ListItemWithIconProps) {
  const mappedIcon = icon ? oldIconMapper[icon] ?? icon : undefined;
  const alignItems = alignmentMapper[iconAlignment] as AlignItems;

  return (
    <StyledListItemWithIcon alignItems={alignItems} {...props}>
      {mappedIcon !== undefined && (
        <Icon
          name={mappedIcon.name || icon}
          weight={mappedIcon.weight || iconWeight}
          color={iconColor}
          width={iconWidth || mappedIcon.width}
          height={iconHeight || mappedIcon.height}
          mr={3}
        />
      )}
      <StyledItem>{children}</StyledItem>
    </StyledListItemWithIcon>
  );
}
